// Mouse, touch and keyboard. Everything a player does to the game goes out as a command through NET().send,
// so solo and online play take exactly the same path.
import { G, regionAt, stationAt, lineAt, upgradeTarget, validSpot, snapToLine } from '../sim/game.js';
import { UI, me, selLine } from './ui-state.js';
import { cvs, toWorld, zoomAt, panBy, resetCam } from './render.js';
import { hud, toast, $ } from './hud.js';
import { Music } from './audio.js';
import { NET, getScreen, actions } from './main.js';

const send = cmd => NET() && NET().send(cmd);
const playing = () => getScreen() === 'play' && G && G.phase === 'play' && me() && me().alive && !UI.demo;
const inputScreen = () => (getScreen() === 'play' || getScreen() === 'draft') && !UI.demo;

// ---------- canvas: build, drag lines, select, pan, pinch ----------
const touches = new Map();
let pinch = null;

function startPan(e, w) { UI.pan = { sx: e.clientX, sy: e.clientY, w, moved: false, button: e.button }; }

cvs.addEventListener('pointerdown', e => {
  if (!G || !inputScreen()) return;
  cvs.setPointerCapture(e.pointerId);
  touches.set(e.pointerId, { x: e.clientX, y: e.clientY });
  if (touches.size === 2) {
    // a second finger turns any gesture into pinch-zoom and pan
    const [a, b] = [...touches.values()];
    pinch = { d: Math.hypot(a.x - b.x, a.y - b.y), cx: (a.x + b.x) / 2, cy: (a.y + b.y) / 2 };
    UI.drag = null; UI.pan = null;
    return;
  }
  const w = toWorld(e);
  if (e.button === 1 || G.phase === 'draft') { startPan(e, w); return; }
  if (!playing()) { startPan(e, w); return; }
  const p = me();
  if (e.button === 2) {
    if (UI.placing) { UI.placing = null; return; }
    const L = lineAt(p, w);
    if (L) { send({ type: 'removeLine', line: L.id }); if (UI.selLine === L.id) UI.selLine = null; }
    return;
  }
  if (UI.placing && (UI.placing.kind === 'warehouse' || UI.placing.kind === 'customs')) {
    const kind = UI.placing.kind, st = upgradeTarget(p, w, kind);
    if (!st) { toast(kind === 'customs' ? 'Click one of your gates (one without customs yet).' : 'Click one of your stations (one without a warehouse yet).'); return; }
    send({ type: 'upgradeStation', kind, station: st.id }); UI.placing = null;
    return;
  }
  if (UI.placing) {
    const snap = snapToLine(p, w), spot = snap ? snap.pos : w;
    if (!validSpot(p, spot)) { toast(snap ? 'Not enough room on that stretch of line.' : 'Too close to the border or another station.'); return; }
    send({ type: 'build', kind: UI.placing.kind, shape: UI.placing.shape, x: w.x, y: w.y });
    UI.placing = null;
    return;
  }
  const s = stationAt(w, UI.seat);
  if (s) { UI.drag = { from: s, cur: w }; return; }
  startPan(e, w);
});

cvs.addEventListener('pointermove', e => {
  if (!G) return;
  if (touches.has(e.pointerId)) touches.set(e.pointerId, { x: e.clientX, y: e.clientY });
  if (pinch && touches.size >= 2) {
    const [a, b] = [...touches.values()], d = Math.hypot(a.x - b.x, a.y - b.y), cx = (a.x + b.x) / 2, cy = (a.y + b.y) / 2;
    panBy(cx - pinch.cx, cy - pinch.cy);
    if (pinch.d > 10) zoomAt(cx, cy, d / pinch.d);
    pinch = { d, cx, cy };
    return;
  }
  if (UI.pan) {
    const dx = e.clientX - UI.pan.sx, dy = e.clientY - UI.pan.sy;
    if (!UI.pan.moved && Math.hypot(dx, dy) > 5) UI.pan.moved = true;
    if (UI.pan.moved) { panBy(dx, dy); UI.pan.sx = e.clientX; UI.pan.sy = e.clientY; cvs.style.cursor = 'grabbing'; return; }
  }
  const w = toWorld(e); UI.mouse = w;
  if (G.phase === 'draft') { const r = regionAt(w); UI.hover = r ? r.id : null; cvs.style.cursor = r ? 'pointer' : 'default'; return; }
  if (UI.drag && playing()) {
    UI.drag.cur = w;
    const s = stationAt(w, UI.seat);
    if (s && s !== UI.drag.from) { send({ type: 'link', a: UI.drag.from.id, b: s.id, line: UI.selLine }); UI.drag.from = s; }
  }
  cvs.style.cursor = UI.placing ? 'crosshair' : playing() && stationAt(w, UI.seat) ? 'grab' : 'default';
});

function endPointer(e) {
  touches.delete(e.pointerId);
  if (touches.size < 2) pinch = null;
  if (!G) return;
  if (UI.pan && !UI.pan.moved && UI.pan.button !== 1) clickAt(UI.pan.w);
  if (UI.drag) send({ type: 'endDrag' });
  UI.pan = null; UI.drag = null;
}
cvs.addEventListener('pointerup', endPointer);
cvs.addEventListener('pointercancel', endPointer);

// a press that never moved is a click: claim a region in the draft, select a line in play
function clickAt(w) {
  if (G.phase === 'draft') { const r = regionAt(w); if (r && inputScreen()) { send({ type: 'claim', rid: r.id }); actions('refreshDraft'); } return; }
  if (playing()) { const L = lineAt(me(), w); UI.selLine = L ? L.id : null; }
}

cvs.addEventListener('wheel', e => {
  if (!G || !inputScreen()) return;
  e.preventDefault();
  // pinch (ctrl+wheel) and notched mouse wheels zoom; two-finger trackpad scrolls pan
  const mouseWheel = e.deltaMode === 1 || (e.deltaX === 0 && Math.abs(e.deltaY) >= 40);
  if (e.ctrlKey || mouseWheel) zoomAt(e.clientX, e.clientY, Math.exp(-e.deltaY * (e.ctrlKey ? 0.01 : 0.0015) * (e.deltaMode === 1 ? 30 : 1)));
  else panBy(-e.deltaX, -e.deltaY);
}, { passive: false });
cvs.addEventListener('contextmenu', e => e.preventDefault());

// ---------- buttons: one delegated handler for every [data-a] ----------
function press(e) {
  const b = e.target.closest && e.target.closest('[data-a]');
  if (!b || b.disabled) return;
  e.stopPropagation();
  const a = b.dataset.a, v = b.dataset.v;
  const handled = actions(a, v, b);
  if (!handled && playing()) gameAction(a, v);
  hud();
}
// a mouse presses on the way down, which feels instant; a finger presses on a finished tap (click),
// so starting a scroll on a button in a menu never sets it off
let lastPointer = 'mouse';
document.addEventListener('pointerdown', e => { lastPointer = e.pointerType; if (e.pointerType === 'mouse') press(e); }, true);
document.addEventListener('click', e => { if (lastPointer !== 'mouse') press(e); }, true);

function gameAction(a, v) {
  const p = me();
  switch (a) {
    case 'target': send({ type: 'target', v: v === 'auto' ? 'auto' : +v }); break;
    case 'rateDown': send({ type: 'rate', delta: -1 }); break;
    case 'rateUp': send({ type: 'rate', delta: 1 }); break;
    case 'upgrade': send({ type: 'upgradeRate' }); break;
    case 'depot': case 'recycler': UI.placing = UI.placing && UI.placing.kind === a && UI.placing.shape === v ? null : { kind: a, shape: v }; UI.panelOpen = false; break;
    case 'warehouse': case 'customs': UI.placing = UI.placing && UI.placing.kind === a ? null : { kind: a }; UI.panelOpen = false; break;   // phones: get the sheet out of the way to place
    case 'bank': send({ type: 'bank' }); break;
    case 'surge': send({ type: 'surge' }); break;
    case 'annex': send({ type: 'annex', rid: +v }); break;
    case 'train': send({ type: 'addTrain', line: UI.selLine }); break;
    case 'carriage': send({ type: 'addCarriage', line: UI.selLine }); break;
    case 'line': send({ type: 'buyLine' }); break;
    case 'sel': UI.selLine = p.lines.some(L => L.id === +v) ? +v : null; break;
    case 'del': send({ type: 'removeLine', line: +v }); if (UI.selLine === +v) UI.selLine = null; break;
    case 'cheatCoins': send({ type: 'cheat', what: 'coins', n: +v }); break;
    case 'cheatKill': send({ type: 'cheat', what: 'kill' }); break;
    case 'cheatRush': send({ type: 'cheat', what: 'rush' }); break;
  }
}

// ---------- keyboard ----------
document.addEventListener('keydown', e => {
  if (e.target.closest && e.target.closest('input')) return;   // typing a name or room code
  const sc = getScreen();
  if ((sc === 'play' || sc === 'draft') && !e.metaKey && !e.ctrlKey && !UI.demo) {
    const pan = { ArrowLeft: [1, 0], a: [1, 0], A: [1, 0], ArrowRight: [-1, 0], d: [-1, 0], D: [-1, 0], ArrowUp: [0, 1], w: [0, 1], W: [0, 1], ArrowDown: [0, -1], s: [0, -1], S: [0, -1] }[e.key];
    if (pan) { panBy(pan[0] * 70, pan[1] * 70); e.preventDefault(); return; }
    if (e.key === '+' || e.key === '=') { zoomAt(innerWidth / 2, innerHeight / 2, 1.25); return; }
    if (e.key === '-' || e.key === '_') { zoomAt(innerWidth / 2, innerHeight / 2, 0.8); return; }
    if (e.key === '0') { resetCam(); return; }
  }
  if ((e.key === 'm' || e.key === 'M') && !e.metaKey && !e.ctrlKey) { Music.toggle(); actions('refreshMenu'); hud(); return; }
  if (e.key === 'Escape') {
    if (sc === 'play' && (UI.placing || UI.drag || UI.selLine != null)) { UI.placing = null; UI.drag = null; UI.selLine = null; return; }
    actions('escape');
    return;
  }
  if ((e.key === 'Delete' || e.key === 'Backspace') && playing() && selLine()) {
    send({ type: 'removeLine', line: UI.selLine }); UI.selLine = null; return;
  }
  if (e.code === 'Space' && sc === 'play' && NET() && NET().kind === 'local') { NET().paused = !NET().paused; e.preventDefault(); }
});

// ---------- quick hover descriptions; elementsFromPoint also finds disabled buttons ----------
let tipFor = null, tipTimer = 0;
addEventListener('pointermove', e => {
  const el = document.elementsFromPoint(e.clientX, e.clientY).find(n => n.dataset && n.dataset.tip);
  const key = el ? el.dataset.tip : null;
  if (key === tipFor) return;
  tipFor = key; clearTimeout(tipTimer);
  if (!el) { $('tip').classList.remove('show'); return; }
  tipTimer = setTimeout(() => {
    const [name, text] = key.split('|'), r = el.getBoundingClientRect(), tip = $('tip');
    $('tipName').textContent = name; $('tipText').textContent = text || '';
    // sit just outside the panel so the tip never covers its own buttons
    const edge = (el.closest('.card') || el).getBoundingClientRect().right, right = edge + 10 + 240 < innerWidth;
    tip.style.left = (right ? edge + 10 : Math.max(8, r.left)) + 'px';
    tip.style.top = (right ? r.top - 4 : r.bottom + 8) + 'px';
    tip.classList.add('show');
  }, 100);
});
